import { useEffect, useState } from "react";
import "./FAQ.css";
import usePageMeta from "../../hooks/usePageMeta";

const faqGroups = [
  {
    heading: "The basics",
    items: [
      {
        q: "What is Money Protocol?",
        a: "Money Protocol is a decentralized, self-custodial Bitcoin liquidity system that lets BTC holders borrow stablecoins against their Bitcoin at 0% interest, without giving up custody. The protocol locks Bitcoin in a smart contract and mints BPD (Bitcoin Protocol Dollar), a Bitcoin-backed stablecoin, against that collateral. Borrowers repay whenever they choose and unlock their original BTC.",
      },
      {
        q: "What is Bitcoin-backed borrowing?",
        a: "Bitcoin-backed borrowing is the practice of using Bitcoin as collateral to obtain dollar-equivalent liquidity without selling the underlying BTC. The borrower deposits BTC, receives a stablecoin or fiat loan against that collateral, and gets the Bitcoin back when the loan is repaid. In a self-custodial protocol like Money Protocol, the entire process is enforced by a smart contract — no bank, no lending desk, no counterparty.",
      },
      {
        q: "How does Bitcoin collateral work?",
        a: "You deposit a defined amount of Bitcoin into the protocol's smart contract, and the contract tracks it as your \"vault\" — your individual loan position. The contract enforces a minimum collateral ratio (typically 110%): the value of your locked BTC must always exceed the value of the stablecoin you borrowed by at least that much. If the ratio falls below the threshold, the smart contract liquidates the vault automatically to keep the system solvent.",
      },
      {
        q: "Can you borrow against Bitcoin without selling it?",
        a: "Yes — that is precisely what Money Protocol enables. You retain ownership of your Bitcoin throughout the loan; the protocol simply locks it as collateral while you spend the borrowed stablecoin. Because no sale occurs, no capital-gains event is triggered (in most jurisdictions; consult your tax advisor for your specific situation).",
      },
      {
        q: "Is Money Protocol controlled by a company or governance?",
        a: "No. The protocol is immutable: the smart contracts have no admin keys, no upgrade mechanism, and no governance token that can vote to change parameters. Once deployed, the rules — collateral ratio, fees, liquidation logic — are fixed in code. There is no team that can pause withdrawals, freeze accounts, or alter the protocol's behaviour. This is a deliberate design choice: the absence of governance removes a class of risk (governance capture, malicious upgrades, regulatory pressure on a controllable team) that exists in most DeFi systems.",
      },
    ],
  },
  {
    heading: "How Money Protocol works",
    items: [
      {
        q: "How does Money Protocol let you borrow at 0% interest?",
        a: "0% interest is structurally possible because the protocol does not lend you anything — it mints a stablecoin against the collateral you posted. There is no third-party lender to pay an APY to and no rehypothecation of your BTC. The protocol covers its costs through a one-time borrow fee at issuance and a redemption mechanism, not through ongoing interest charges.",
      },
      {
        q: "What are BPD and MP?",
        a: "BPD is the protocol's USD-pegged stablecoin, minted against Bitcoin collateral. MP is the protocol's secondary token, which captures fee revenue and is rewarded to early adopters and stability-pool depositors. BPD is what borrowers receive; MP is the incentive token aligned with long-term protocol health.",
      },
      {
        q: "What are the steps to borrow against Bitcoin with Money Protocol?",
        a: "Connect a self-custodial wallet, deposit BTC into the protocol contract to open a vault, choose a collateral ratio that gives you adequate safety margin, and mint the BPD stablecoin against your collateral. You receive BPD in your wallet, pay a one-time issuance fee, and can use BPD as you would any stablecoin. To close the loan, return the BPD plus any redemption fee and your Bitcoin is unlocked.",
      },
      {
        q: "What is a vault?",
        a: "A vault is your individual borrowing position in Money Protocol — the smart-contract record of how much Bitcoin you've deposited as collateral and how much BPD you've minted against it. Each vault is isolated: your collateral and debt are separate from every other borrower's. You can adjust your vault (add collateral, repay debt, mint more BPD) at any time as long as it remains above the minimum collateral ratio.",
      },
      {
        q: "What is the collateral ratio in Money Protocol?",
        a: "The collateral ratio is the value of your locked Bitcoin divided by the value of your minted BPD, expressed as a percentage. Money Protocol's minimum ratio is 110% in normal operation — your BTC must be worth at least 1.1× the BPD you've borrowed. Most borrowers maintain ratios well above this (200–400%) to give themselves room for Bitcoin price drops.",
      },
      {
        q: "How is BPD redeemed for Bitcoin?",
        a: "BPD holders can redeem their stablecoin directly against the protocol for Bitcoin at face value — 1 BPD always claims $1 worth of BTC, minus a redemption fee. Redemptions are processed against the riskiest vaults first (those with the lowest collateral ratios), which is the mechanism that keeps BPD pegged to the dollar: if BPD trades below $1 on the open market, anyone can buy it cheaply and redeem it for a full dollar of BTC, which arbitrages the price back toward peg.",
      },
    ],
  },
  {
    heading: "Costs, fees, and revenue",
    items: [
      {
        q: "How much does it cost to borrow with Money Protocol?",
        a: "Costs come from a one-time borrow fee at the moment you mint BPD plus a redemption fee that varies algorithmically based on system conditions. There is no recurring interest charge that compounds against your debt over time. The exact fee schedule is published in the Money Protocol documentation.",
      },
      {
        q: "What are the minimum and maximum borrow and redemption fees?",
        a: "Both the borrow fee and the redemption fee adjust algorithmically based on recent redemption activity — heavier redemptions push fees up, periods of low activity let them decay back down. The borrow fee has a floor of 0.5% and a cap of 5% of the BPD minted. The redemption fee starts at 0.5% and rises with redemption volume. Both are one-time fees applied at the moment of action; there is no recurring component.",
      },
      {
        q: "Are there ongoing interest charges?",
        a: "No. Money Protocol charges 0% ongoing interest on outstanding loans. The only costs are the one-time borrow fee and any redemption fee, both transparent and on-chain.",
      },
      {
        q: "How does the protocol earn revenue if there is no interest?",
        a: "Revenue comes from the one-time borrow fee charged when BPD is minted and redemption fees collected when BPD holders redeem against vaults. These fees flow to MP token stakers and the stability pool, aligning protocol revenue with the people who provide the system's safety backstop. The protocol does not need a recurring interest stream because it does not redeploy borrower collateral.",
      },
    ],
  },
  {
    heading: "Risks and safety",
    items: [
      {
        q: "Is it safe to borrow against Bitcoin?",
        a: "The risks of self-custodial Bitcoin-backed borrowing are bounded and transparent: liquidation risk if BTC's price drops below your vault's threshold, and smart-contract risk because the protocol is code. Both can be mitigated — by keeping a healthy collateral buffer, and by using protocols that have multiple independent audits and a long deployment history. The class of risk in self-custodial borrowing is fundamentally different from custodial lending, where the failure mode is the company itself going insolvent.",
      },
      {
        q: "What happens if the Bitcoin price drops after I borrow?",
        a: "If your vault's collateral ratio falls below 110%, the protocol automatically liquidates it: your Bitcoin collateral is sold (technically, transferred to the stability pool) and your BPD debt is wiped. To avoid liquidation during volatility, borrow conservatively — most users mint at ratios well above the minimum so a 30%+ BTC drawdown can occur before liquidation risk becomes meaningful. You can also add collateral or repay debt at any time to raise your ratio.",
      },
      {
        q: "How does crypto collateral liquidation work in Money Protocol?",
        a: "Liquidation is rule-based and automatic: when your vault's ratio drops below 110%, anyone can trigger the liquidation by calling the protocol's contract. The collateral is transferred to the stability pool (which is funded by users who voluntarily deposit BPD), the depositors absorb your BPD debt at a discount to your BTC value, and the system stays solvent. There is no human in the loop and no margin-call email.",
      },
      {
        q: "What is the liquidation threshold?",
        a: "The minimum collateral ratio for a vault in Money Protocol is 110% under normal operation — meaning if your locked BTC's value drops to less than 1.1× your BPD debt, the vault becomes eligible for liquidation. During a market-wide stress event, the protocol can enter recovery mode, in which the threshold rises to 150% to protect overall system solvency.",
      },
      {
        q: "What is recovery mode?",
        a: "Recovery mode is a system-wide safety state that activates when the total collateral ratio across all vaults drops below 150%. In recovery mode, the protocol raises individual liquidation thresholds and prioritizes restoring system health. This is the mechanism that lets Money Protocol survive severe BTC drawdowns without becoming insolvent, even at low individual collateral ratios.",
      },
      {
        q: "Can I lose money using Money Protocol?",
        a: "Yes — the same way you can lose money with any leveraged position. If Bitcoin's price falls hard enough to liquidate your vault, you lose your collateral but your debt is cleared. The way to manage this is conservative borrowing (maintain a high collateral ratio) and active monitoring during volatile periods. The protocol's smart-contract failure modes are bounded by its audited code; counterparty failure modes that exist with CeFi lenders do not exist here.",
      },
    ],
  },
  {
    heading: "Comparisons",
    items: [
      {
        q: "How is Money Protocol different from CeFi lenders like Nexo or the old BlockFi?",
        a: "CeFi lenders take custody of your Bitcoin, redeploy it for yield, and pay you a portion. That model collapsed publicly in 2022 (Celsius, BlockFi, Voyager, Genesis), exposing the structural problem: when a counterparty holds your collateral, your funds depend on their continuing solvency. Money Protocol holds your Bitcoin in a smart contract you can audit; the protocol's failure modes are bounded by code, not by a CEO's judgment.",
      },
      {
        q: "How is Money Protocol different from Aave?",
        a: "Aave is a multi-asset money market on Ethereum where lenders supply assets and earn interest paid by borrowers. Money Protocol is a single-collateral (Bitcoin) borrowing system that mints a stablecoin against collateral at 0% interest, with the cost paid as a one-time fee. The two are complementary, not equivalent: Aave fits short-term variable-rate borrowing with diverse collateral; Money Protocol fits long-term, predictable, Bitcoin-collateralized borrowing.",
      },
      {
        q: "Why borrow with Money Protocol instead of selling Bitcoin?",
        a: "Selling triggers a tax event (in most jurisdictions) and permanently shrinks your Bitcoin position. Borrowing against your Bitcoin gives you dollar liquidity without either consequence: you keep the position, get the dollars, and pay a one-time fee instead of capital-gains tax. The trade-off is that you take on liquidation risk while the loan is open — a risk you control by setting a conservative collateral ratio.",
      },
    ],
  },
  {
    heading: "Tokens, rewards, and revenue",
    items: [
      {
        q: "What is the MP token used for?",
        a: "MP captures protocol fee revenue and is awarded to early adopters and stability-pool depositors. Staking MP entitles holders to a share of the borrow fees and redemption fees that flow through the system. MP aligns long-term protocol health with the participants who provide the system's safety backstop.",
      },
      {
        q: "What is the total supply of MP?",
        a: "MP has a hard-capped total supply of 500 million tokens. There is no inflation mechanism beyond the predefined emission schedule, which distributes MP to stability-pool depositors, early adopters, and the protocol's long-term incentive programs. Once the schedule is complete, no further MP can be minted.",
      },
      {
        q: "How does BPD stay pegged to the US dollar?",
        a: "BPD's peg is enforced by direct redemption arbitrage rather than by a central reserve or an algorithmic rebase. If BPD trades below $1, anyone can buy the discounted BPD on the open market and redeem it 1:1 against the protocol for $1 of BTC — capturing the spread and pulling BPD's market price back toward peg. The 110% minimum collateral ratio guarantees that every BPD in circulation is backed by more than its face value in Bitcoin, so the redemption right is always credible.",
      },
      {
        q: "How can I earn rewards through Money Protocol?",
        a: "You can earn rewards by depositing BPD into the stability pool (which absorbs liquidations and earns the discount on liquidated collateral), by staking MP tokens (which earns a share of protocol fees), and by participating in early-adoption incentive programs. Each path has different risk and time-commitment characteristics; the documentation describes them in detail.",
      },
      {
        q: "What is the stability pool?",
        a: "The stability pool is the protocol's primary liquidation backstop. BPD holders deposit their tokens into the pool; when vaults are liquidated, the pool's BPD is used to wipe the bad debt and the depositors receive the liquidated BTC at a discount. This generates returns for stability-pool depositors and keeps the protocol solvent without external counterparties.",
      },
    ],
  },
  {
    heading: "Use cases",
    items: [
      {
        q: "Who should use Money Protocol?",
        a: "Three groups benefit most: long-term Bitcoin holders who need episodic dollar liquidity but don't want to sell; operators with Bitcoin on their balance sheet who need working capital without a tax event; and DeFi-literate users who refuse to use CeFi lenders after 2022. The common thread is wanting purchasing power from a Bitcoin position without giving up the position itself.",
      },
      {
        q: "What can I do with the borrowed BPD stablecoin?",
        a: "BPD is fungible and can be used wherever stablecoins are accepted: swap it for USD on a centralized exchange, bridge it to other chains, deploy it in DeFi protocols, pay vendors who accept stablecoins, or simply hold it as a dollar-denominated reserve. There is no restriction on how the borrowed BPD is used.",
      },
      {
        q: "Can I repay my Money Protocol loan early?",
        a: "Yes — there is no fixed loan term. Return the BPD you borrowed (plus any applicable redemption fee) at any time, and your Bitcoin collateral is released back to your wallet. There is no penalty for early repayment.",
      },
      {
        q: "How do I get liquidity from my Bitcoin without selling it?",
        a: "Use a self-custodial Bitcoin-backed lending protocol like Money Protocol: deposit Bitcoin as collateral, mint a USD-pegged stablecoin against it, and use the stablecoin for whatever you need. When you have the funds, repay the stablecoin and unlock your original Bitcoin. The whole process is enforced by smart contracts — no application, no credit check, no counterparty.",
      },
    ],
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqGroups.flatMap((group) =>
    group.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  ),
};

export default function FAQ() {
  usePageMeta(
    "Bitcoin Loan FAQs | BTC Collateral, Interest & Security",
    "Find answers about Bitcoin-backed loans, BTC collateral, interest rates, security, and borrowing process with Money Protocol. Learn how to borrow safely.",
    "https://www.moneyprotocol.co/faq",
  );

  const [openItems, setOpenItems] = useState(new Set());

  const toggleItem = (key) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(faqSchema);
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, []);

  return (
    <div className="container">
      <div className="d-flex flex-column faq-container gap-24">
        <h1 className="mb-26">Frequently Asked Questions (FAQ)</h1>

        {faqGroups.map((group, gi) => (
          <section key={gi} className="faq-group">
            <h2 className="faq-group-heading">{group.heading}</h2>
            <div className="d-flex flex-column gap-16">
              {group.items.map((item, ii) => {
                const key = `${gi}-${ii}`;
                const isOpen = openItems.has(key);
                return (
                  <div
                    key={key}
                    className={`bg-white curved faq-item ${isOpen ? "open" : ""}`}
                  >
                    <button
                      type="button"
                      className="faq-question"
                      aria-expanded={isOpen}
                      aria-controls={`faq-answer-${key}`}
                      onClick={() => toggleItem(key)}
                    >
                      <h3 className="faq-card-header m-0">{item.q}</h3>
                      <span
                        className={`faq-chevron ${isOpen ? "open" : ""}`}
                        aria-hidden="true"
                      >
                        ▾
                      </span>
                    </button>
                    {isOpen && (
                      <div
                        id={`faq-answer-${key}`}
                        className="faq-answer"
                        role="region"
                      >
                        <p className="card-desc m-0">{item.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        <p className="faq-cta">
          Ready to start?{" "}
          <a
            href="https://docs.moneyprotocol.co/docs/intro"
            target="_blank"
            rel="noopener noreferrer"
          >
            Read the full documentation
          </a>{" "}
          or{" "}
          <a
            href="https://www.bitcoinstable.money/"
            target="_blank"
            rel="noopener noreferrer"
          >
            launch Money Protocol
          </a>{" "}
          to open your first vault.
        </p>
      </div>
    </div>
  );
}
